// File: src/lib/graphql/queries/timesheet.ts
// Purpose: Timesheet Management GraphQL Queries and Mutations
// VIBE - Timesheet API

import { gql } from 'graphql-request'
import { executeGraphQL } from '../client'
import { BulkApproveTimeSheetsResponse, CreateTimeSheetInput, DeleteTimeSheetResponse, EmployeeTimesheetItem, MyTimesheets, TimesheetItem, UpdateTimeSheetInput } from '@/types/api'

// ============================================
// TYPES
// ============================================

export interface Timesheet {
  id: string
  employee: {
    id: string
    firstName: string
    lastName: string
    employeeId: string
  }
  date: string
  checkInTime?: string
  checkOutTime?: string
  totalHours: number
  breakHours: number
  workHours: number
  overtimeHours: number
  project?: {
    id: string
    name: string
    code: string
  }
  task?: {
    id: string
    title: string
  }
  description?: string
  submitStatus: TimesheetStatus
  approvedBy?: {
    id: string
    firstName: string
    lastName: string
  }
  approvedDate?: string
  comments?: string
  createdAt: string
  updatedAt: string
}

export type TimesheetStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'cancelled'

export interface TimesheetSummary {
  totalHours: number
  workHours: number
  overtimeHours: number
  approvedHours: number
  pendingHours: number
  currentWeekHours: number
  currentMonthHours: number
  averageDailyHours: number
}

export interface BulkApproveTimesheetsInput {
  ids: string[]
  approvalStatus: string
  notes?: string
}

// ============================================
// QUERIES
// ============================================

export const TIMESHEETS_QUERY = gql`
  query Timesheets($limit: Int, $filter: TimeSheetFilterInput) {
    timesheets(limit: $limit, filter: $filter) {
      _id
      id
      date
      checkIn
      checkOut
      breakDuration
      totalHours
      overtime
      status
      notes
      submitStatus
      submittedAt
      approvalStatus
      approvalNotes
      createdAt
      updatedAt
      tasks {
        title
        description
        hours
        status
        startTime
        endTime
      }
      employee {
        _id
        firstName
        lastName
        email
        hourlyRate
        overtimeRate
        employee_left
        leaving_date
      }
      project {
          _id
          name
          code
          description
          status
      }
    }
  }
`

export const EMPLOYEE_TIMESHEETS_QUERY = gql`
  query EmployeeTimesheets($employeeId: ID!, $startDate: Date, $endDate: Date, $status: TimesheetStatus) {
    employeeTimesheets(employeeId: $employeeId, startDate: $startDate, endDate: $endDate, status: $status) {
      _id
      employee {
        _id
        firstName
        lastName
        email
      }
      date
      checkIn
      checkOut
      totalHours
      breakHours
      workHours
      overtimeHours
      project {
        _id
        name
        code
      }
      task {
        _id
        title
      }
      description
      status
      createdAt
      updatedAt
    }
  }
`

export const TIMESHEET_QUERY = gql`
  query Timesheet($id: ID!) {
    timesheet(id: $id) {
      id
      employee {
        id
        firstName
        lastName
        employeeId
        email
      }
      date
      checkInTime
      checkOutTime
      totalHours
      breakHours
      workHours
      overtimeHours
      project {
        id
        name
        code
      }
      task {
        id
        title
        description
      }
      description
      status
      approvedBy {
        id
        firstName
        lastName
      }
      approvedDate
      comments
      createdAt
      updatedAt
    }
  }
`

export const MY_TIMESHEETS_QUERY = gql`
  query MyTimesheets($filter: TimeSheetFilterInput) {
    myTimesheets(filter: $filter) {
      _id
      id
      date
      checkIn
      checkOut
      breakDuration
      totalHours
      overtime
      status
      notes
      submitStatus
      submittedAt
      approvalStatus
      approvalNotes
      createdAt
      updatedAt
      employee {
          _id
          firstName
          lastName
          email
      }
      tasks {
          title
          description
          hours
          status
          startTime
          endTime
        }
    }
  }
`

export const TIMESHEET_SUMMARY_QUERY = gql`
  query TimesheetSummary($employeeId: ID!, $startDate: String!, $endDate: String!) {
    timesheetSummary(employeeId: $employeeId, startDate: $startDate, endDate: $endDate) {
      totalHours
      totalOvertime
      totalDays
      presentDays
      absentDays
      leaveDays
      holidays
      averageHoursPerDay
    }
  }
`

export const TODAY_TIMESHEET_QUERY = gql`
  query TodayTimesheet {
    todayTimesheet {
      id
      date
      checkInTime
      checkOutTime
      totalHours
      workHours
      project {
        name
      }
      task {
        title
      }
      status
    }
  }
`

// ============================================
// MUTATIONS
// ============================================

export const CREATE_TIMESHEET_MUTATION = gql`
  mutation CreateTimeSheet($input: TimeSheetInput!) {
    createTimeSheet(input: $input) {
      _id
      id
      date
      checkIn
      checkOut
      breakDuration
      totalHours
      overtime
      status
      notes
      submitStatus
      submittedAt
      approvalStatus
      approvalNotes
      createdAt
      updatedAt
      project {
        _id
        name
        code
        description
        status
        priority
        startDate
        endDate
        actualStartDate
        actualEndDate
        tags
        progress
        is_active
        is_deleted
        createdAt
        updatedAt
      }
      tasks {
        title
        description
        hours
        status
        startTime
        endTime
      }
    }
  }
`

export const UPDATE_TIMESHEET_MUTATION = gql`
  mutation UpdateTimeSheet($id: ID!, $input: TimeSheetUpdateInput!) {
    updateTimeSheet(id: $id, input: $input) {
      _id
      id
      date
      checkIn
      checkOut
      breakDuration
      totalHours
      overtime
      status
      notes
      submitStatus
      submittedAt
      approvalStatus
      approvalNotes
      createdAt
      updatedAt
      project {
        _id
        name
        code
        description
        status
        priority
        startDate
        endDate
        actualStartDate
        actualEndDate
        tags
        progress
        is_active
        is_deleted
        createdAt
        updatedAt
      }
      tasks {
        title
        description
        hours
        status
        startTime
        endTime
      }
    }
  }
`

export const DELETE_TIMESHEET_MUTATION = gql`
  mutation DeleteTimeSheet($id: ID!) {
    deleteTimeSheet(id: $id)
  }
`

export const CHECK_IN_MUTATION = gql`
  mutation CheckIn($projectId: ID, $taskId: ID, $description: String) {
    checkIn(projectId: $projectId, taskId: $taskId, description: $description) {
      id
      date
      checkInTime
      status
    }
  }
`

export const CHECK_OUT_MUTATION = gql`
  mutation CheckOut($timesheetId: ID!, $description: String) {
    checkOut(timesheetId: $timesheetId, description: $description) {
      id
      checkOutTime
      totalHours
      workHours
      status
    }
  }
`


export const BULK_APPROVE_TIMESHEETS_MUTATION = gql`
  mutation BulkApproveTimeSheets($ids: [ID!]!, $approvalStatus: ApprovalStatus!, $notes: String) {
    bulkApproveTimeSheets(ids: $ids, approvalStatus: $approvalStatus, notes: $notes) {
      _id
      id
      date
      checkIn
      checkOut
      breakDuration
      totalHours
      overtime
      status
      notes
      submitStatus
      submittedAt
      approvalStatus
      approvalNotes
      createdAt
      updatedAt
      employee {
        _id
        firstName
        lastName
        email
      }
      project {
        _id
        name
        code
        description
        status
        priority
        startDate
        endDate
        actualStartDate
        actualEndDate
        tags
        progress
        is_active
        is_deleted
        createdAt
        updatedAt
      }
      tasks {
        title
        description
        hours
        status
        startTime
        endTime
        project {
          _id
          name
          code
          description
          status
          priority
          startDate
          endDate
          actualStartDate
          actualEndDate
          tags
          progress
          is_active
          is_deleted
          createdAt
          updatedAt
        }
      }
    }
  }
`

export const SUBMIT_TIMESHEET_MUTATION = gql`
  mutation SubmitTimeSheet($id: ID!) {
    submitTimeSheet(id: $id) {
      _id
      id
      date
      checkIn
      checkOut
      breakDuration
      totalHours
      overtime
      status
      notes
      submitStatus
      submittedAt
      approvalStatus
      approvalNotes
      createdAt
      updatedAt
    }
  }
`

export const APPROVE_TIMESHEET_MUTATION = gql`
  mutation ApproveTimeSheet($id: ID!, $input: TimeSheetApprovalInput!) {
    approveTimeSheet(id: $id, input: $input) {
      _id
      id
      date
      checkIn
      checkOut
      breakDuration
      totalHours
      overtime
      status
      notes
      submitStatus
      submittedAt
      approvalStatus
      approvalNotes
      createdAt
      updatedAt
    }
  }
`

// ============================================
// API SERVICE CLASS
// ============================================

export class TimesheetAPI {
  // Timesheet Queries
  static async getTimesheets(limit: number = 10, filter?: { employee?: string | null, startDate?: string | null, endDate?: string | null }) {
  const { timesheets } = await executeGraphQL<{ timesheets: TimesheetItem[] }>(
    TIMESHEETS_QUERY,
    { limit, filter: filter || {} }
  )
  return timesheets
}


  static async getEmployeeTimesheets(employeeId: string, startDate?: string, endDate?: string, status?: TimesheetStatus) {
    const { employeeTimesheets } = await executeGraphQL<{ employeeTimesheets: EmployeeTimesheetItem[] }>(
      EMPLOYEE_TIMESHEETS_QUERY,
      { employeeId, startDate, endDate, status }
    )
    return employeeTimesheets
  }

  static async getTimesheet(id: string): Promise<Timesheet> {
    const { timesheet } = await executeGraphQL<{ timesheet: Timesheet }>(
      TIMESHEET_QUERY,
      { id }
    )
    return timesheet
  }

  static async getMyTimesheets({startDate, endDate, status}:{startDate?: string, endDate?: string, status?: TimesheetStatus}) {
    const filter: any = {}
    if (startDate) filter.startDate = startDate
    if (endDate) filter.endDate = endDate
    if (status) filter.status = status

    const { myTimesheets } = await executeGraphQL<{ myTimesheets: any }>(
      MY_TIMESHEETS_QUERY,
      { filter }
    )
    return myTimesheets
  }

  static async getTimesheetSummary(employeeId?: string, startDate?: string, endDate?: string): Promise<TimesheetSummary> {
    const { timesheetSummary } = await executeGraphQL<{ timesheetSummary: TimesheetSummary }>(
      TIMESHEET_SUMMARY_QUERY,
      { employeeId, startDate, endDate }
    )
    return timesheetSummary
  }

  static async getTodayTimesheet(): Promise<Timesheet | null> {
    const { todayTimesheet } = await executeGraphQL<{ todayTimesheet: Timesheet | null }>(
      TODAY_TIMESHEET_QUERY
    )
    return todayTimesheet
  }

  // Timesheet Mutations
  static async createTimesheet(input: CreateTimeSheetInput): Promise<Timesheet> {
    const { createTimeSheet } = await executeGraphQL<{ createTimeSheet: Timesheet }>(
      CREATE_TIMESHEET_MUTATION,
      { input }
    )
    return createTimeSheet
  }

  static async updateTimesheet(id: string, input: any): Promise<Timesheet> {
    const { updateTimeSheet } = await executeGraphQL<{ updateTimeSheet: Timesheet }>(
      UPDATE_TIMESHEET_MUTATION,
      { id, input }
    )
    return updateTimeSheet
  }

  static async deleteTimesheet(id: string): Promise<boolean> {
    const { deleteTimeSheet } = await executeGraphQL<{ deleteTimeSheet: boolean }>(
      DELETE_TIMESHEET_MUTATION,
      { id }
    )
    return deleteTimeSheet
  }

  static async checkIn(projectId?: string, taskId?: string, description?: string): Promise<Timesheet> {
    const { checkIn } = await executeGraphQL<{ checkIn: Timesheet }>(
      CHECK_IN_MUTATION,
      { projectId, taskId, description }
    )
    return checkIn
  }

  static async checkOut(timesheetId: string, description?: string): Promise<Timesheet> {
    const { checkOut } = await executeGraphQL<{ checkOut: Timesheet }>(
      CHECK_OUT_MUTATION,
      { timesheetId, description }
    )
    return checkOut
  }

  static async submitTimesheet(id: string): Promise<any> {
    const { submitTimeSheet } = await executeGraphQL<{ submitTimeSheet: any }>(
      SUBMIT_TIMESHEET_MUTATION,
      { id }
    )
    return submitTimeSheet
  }

  static async approveTimesheet(id: string, input: { approvalNotes?: string; approvalStatus?: string }): Promise<any> {
    const { approveTimeSheet } = await executeGraphQL<{ approveTimeSheet: any }>(
      APPROVE_TIMESHEET_MUTATION,
      { id, input }
    )
    return approveTimeSheet
  }

  static async bulkApproveTimesheets(ids: string[], approvalStatus: string, notes?: string): Promise<any[]> {
    const { bulkApproveTimeSheets } = await executeGraphQL<{ bulkApproveTimeSheets: any[] }>(
      BULK_APPROVE_TIMESHEETS_MUTATION,
      { ids, approvalStatus, notes }
    )
    return bulkApproveTimeSheets
  }


  // Alias for dashboard compatibility - calls getMyTimesheets
  // static async getEmployeeTimesheets(startDate?: string, endDate?: string, status?: TimesheetStatus) {
  //   return TimesheetAPI.getMyTimesheets(startDate, endDate, status)
  // }
}